import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";

export default function RegisterPage() {
  const [role, setRole] = useState("company");
  const [form, setForm] = useState({ email: "", password: "", full_name: "", company_name: "", phone: "" });
  const [loading, setLoading] = useState(false);
  // If the visitor came from the Check-up Light, we pre-fill awareness banner +
  // forward the session_token to the backend so the AI analysis is auto-claimed.
  const [lightSessionToken, setLightSessionToken] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const t = localStorage.getItem("cxoora_light_session");
      const r = localStorage.getItem("cxoora_light_result");
      if (t && r) setLightSessionToken(t);
    } catch (_) { /* localStorage unavailable */ }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, role };
      if (lightSessionToken && role === "company") {
        payload.light_checkup_session_token = lightSessionToken;
      }
      await register(payload);
      toast.success("Registrazione completata!");
      // Companies: go to full check-up (will be pre-filled from light analysis if present)
      navigate(role === "company" ? "/app/checkup" : "/per-i-manager/candidati");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore di registrazione");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F6F8FC]">
      <div className="w-full max-w-lg cxo-card p-6 sm:p-8">
        <Logo />
        <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Crea un account</h1>
        <p className="mt-1 text-sm text-slate-600">Scegli la tipologia di registrazione e completa i dati.</p>

        {lightSessionToken && role === "company" && (
          <div
            className="mt-4 rounded-lg border border-[#2D6BFF]/30 bg-[#EAF0FF]/40 p-3 flex items-start gap-2"
            data-testid="register-light-banner"
          >
            <Sparkles size={16} className="text-[#2D6BFF] shrink-0 mt-0.5" />
            <p className="text-xs text-slate-700 leading-relaxed">
              <Badge className="bg-[#2D6BFF] text-white hover:bg-[#1F57F2] mr-1.5 align-middle">AI Check-up</Badge>
              La tua analisi gratuita verrà collegata al tuo account e <strong>pre-compilerà</strong> automaticamente il check-up completo.
            </p>
          </div>
        )}

        <Tabs value={role} onValueChange={setRole} className="mt-5">
          <TabsList className="w-full">
            <TabsTrigger value="company" className="flex-1" data-testid="register-tab-company">Azienda</TabsTrigger>
            <TabsTrigger value="manager" className="flex-1" data-testid="register-tab-manager">Manager</TabsTrigger>
          </TabsList>
        </Tabs>
        <form onSubmit={submit} className="mt-5 space-y-3">
          {role === "company" && (
            <div>
              <Label>Ragione sociale</Label>
              <Input required value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} placeholder="Mia Azienda Srl" data-testid="register-company-name" />
            </div>
          )}
          <div>
            <Label>Nome completo</Label>
            <Input required value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} placeholder="Mario Rossi" data-testid="register-full-name" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="mario@miaazienda.it" data-testid="register-email" />
          </div>
          <div>
            <Label>Telefono (opzionale)</Label>
            <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+39 333 1234567" />
          </div>
          <div>
            <Label>Password (min 6 caratteri)</Label>
            <Input type="password" required minLength={6} value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="••••••••" data-testid="register-password" />
          </div>
          <Button type="submit" className="w-full bg-[#2D6BFF] hover:bg-[#1F57F2] text-white h-11" disabled={loading} data-testid="register-submit">
            {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : null} Registrati
          </Button>
          <p className="text-xs text-slate-500 text-center">Registrandoti accetti i nostri <Link to="/termini" className="underline">Termini</Link> e la <Link to="/privacy" className="underline">Privacy Policy</Link>.</p>
        </form>
        <p className="mt-5 text-sm text-slate-600 text-center">Hai già un account? <Link to="/login" className="font-semibold text-[#2D6BFF]">Accedi</Link></p>
      </div>
    </div>
  );
}
